import api from './api';

// =====================
// KPI CRUD Operations
// =====================

export async function createKPI(payload) {
  const body = {
    kpiName: String(payload.kpiName || '').trim(),
    description: payload.description ? String(payload.description).trim() : null,
    unitOfMeasure: Number(payload.unitOfMeasure),
    kpiType: payload.kpiType || 'TYPE1',
  };
  return api.post('/kpi', body);
}

// GET /kpi is paginated ({ data, meta }); callers expect a plain array of every KPI.
export async function getAllKPIs() {
  const first = await api.get('/kpi');
  if (Array.isArray(first)) return first;

  const items = Array.isArray(first?.data) ? [...first.data] : [];
  const totalPages = Number(first?.meta?.totalPages) || 1;
  const limit = Number(first?.meta?.limit) || items.length || 50;

  if (totalPages > 1) {
    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        api.get(`/kpi?page=${i + 2}&limit=${limit}`),
      ),
    );
    rest.forEach((res) => {
      if (Array.isArray(res?.data)) items.push(...res.data);
    });
  }
  return items;
}

export async function getKPIById(id) {
  if (id == null) throw new Error('KPI ID is required');
  return api.get(`/kpi/${id}`);
}

export async function updateKPI(id, payload) {
  if (id == null) throw new Error('KPI ID is required');
  const body = {};
  if (payload.kpiName !== undefined) body.kpiName = String(payload.kpiName).trim();
  if (payload.description !== undefined) body.description = payload.description ? String(payload.description).trim() : null;
  if (payload.unitOfMeasure !== undefined) body.unitOfMeasure = Number(payload.unitOfMeasure);
  if (payload.kpiType !== undefined) body.kpiType = payload.kpiType;
  return api.patch(`/kpi/${id}`, body);
}

export async function deleteKPI(id) {
  if (id == null) throw new Error('KPI ID is required');
  return api.delete(`/kpi/${id}`);
}

// =====================
// UoM CRUD Operations
// =====================

export async function createUoM(payload) {
  const body = {
    uomValue: String(payload.uomValue || '').trim(),
    description: payload.description ? String(payload.description).trim() : null,
  };
  return api.post('/kpi/uom', body);
}

export async function getAllUoMs() {
  return api.get('/kpi/uom/all');
}

export async function getUoMById(id) {
  if (id == null) throw new Error('UoM ID is required');
  return api.get(`/kpi/uom/${id}`);
}

export async function updateUoM(id, payload) {
  if (id == null) throw new Error('UoM ID is required');
  const body = {};
  if (payload.uomValue !== undefined) body.uomValue = String(payload.uomValue).trim();
  if (payload.description !== undefined) body.description = payload.description ? String(payload.description).trim() : null;
  return api.patch(`/kpi/uom/${id}`, body);
}

export async function deleteUoM(id) {
  if (id == null) throw new Error('UoM ID is required');
  return api.delete(`/kpi/uom/${id}`);
}

// =====================
// KPI Attribute CRUD Operations
// =====================

export async function createAttribute(payload) {
  const body = {
    attributeValue: String(payload.attributeValue || '').trim(),
  };
  return api.post('/kpi/attribute', body);
}

export async function getAllAttributes() {
  return api.get('/kpi/attribute/all');
}

export async function getAttributeById(id) {
  if (id == null) throw new Error('Attribute ID is required');
  return api.get(`/kpi/attribute/${id}`);
}

export async function updateAttribute(id, payload) {
  if (id == null) throw new Error('Attribute ID is required');
  const body = {};
  if (payload.attributeValue !== undefined) body.attributeValue = String(payload.attributeValue).trim();
  return api.patch(`/kpi/attribute/${id}`, body);
}

export async function deleteAttribute(id) {
  if (id == null) throw new Error('Attribute ID is required');
  return api.delete(`/kpi/attribute/${id}`);
}

// =====================
// Process KPI CRUD Operations
// =====================

export const KPI_TYPES = {
  TYPE1: 'TYPE1', // Select from KPI table
  TYPE2: 'TYPE2', // Free entry with custom_kpi_name
};

export const KPI_TYPE_LABELS = {
  TYPE1: 'Standard',
  TYPE2: 'Custom',
};

export async function getAllProcessKPIs() {
  return api.get('/kpi/process-kpi/all');
}

export async function getKPIsByProcessId(processId) {
  if (processId == null) throw new Error('Process ID is required');
  return api.get(`/kpi/process-kpi/process/${processId}`);
}

export async function createProcessKPI(payload) {
  const body = {
    process_id: Number(payload.process_id),
    value: String(payload.value || '').trim(),
    kpiType: payload.kpiType || KPI_TYPES.TYPE1,
  };

  if (body.kpiType === KPI_TYPES.TYPE1) {
    // TYPE1: Select from KPI table
    if (payload.kpi_id == null) throw new Error('kpi_id is required for TYPE1');
    body.kpi_id = Number(payload.kpi_id);
  } else {
    // TYPE2: Free entry
    if (!payload.custom_kpi_name) throw new Error('custom_kpi_name is required for TYPE2');
    body.custom_kpi_name = String(payload.custom_kpi_name).trim();
    if (payload.custom_uom) {
      body.custom_uom = String(payload.custom_uom).trim();
    }
  }

  return api.post('/kpi/process-kpi', body);
}

export async function updateProcessKPI(id, payload) {
  if (id == null) throw new Error('Process KPI ID is required');
  const body = {};

  if (payload.value !== undefined) body.value = String(payload.value).trim();
  if (payload.kpiType !== undefined) body.kpiType = payload.kpiType;

  if (payload.kpiType === KPI_TYPES.TYPE1) {
    if (payload.kpi_id !== undefined) body.kpi_id = Number(payload.kpi_id);
    // Clear TYPE2 fields
    body.custom_kpi_name = null;
    body.custom_uom = null;
  } else if (payload.kpiType === KPI_TYPES.TYPE2) {
    if (payload.custom_kpi_name !== undefined) body.custom_kpi_name = String(payload.custom_kpi_name).trim();
    if (payload.custom_uom !== undefined) body.custom_uom = payload.custom_uom ? String(payload.custom_uom).trim() : null;
    // Clear TYPE1 fields
    body.kpi_id = null;
  }

  return api.patch(`/kpi/process-kpi/${id}`, body);
}

export async function deleteProcessKPI(id) {
  if (id == null) throw new Error('Process KPI ID is required');
  return api.delete(`/kpi/process-kpi/${id}`);
}
